import type { ActionType, TriggerType } from '../../api/types'

export const TRIGGER_LABELS: Record<TriggerType, string> = {
  'task.created': 'Aufgabe erstellt',
  'task.updated': 'Aufgabe geändert',
  'task.moved': 'Aufgabe verschoben',
  'task.status_changed': 'Status geändert',
  'label.added': 'Label hinzugefügt',
  'label.removed': 'Label entfernt',
  'task.user_assigned': 'Benutzer zugewiesen',
  'task.role_assigned': 'Rolle zugewiesen',
  'task.comment_created': 'Kommentar erstellt',
  'project.status_changed': 'Projektstatus geändert',
  'task.due_in_24h': 'Fälligkeitsdatum in < 24h',
  'task.overdue': 'Aufgabe überfällig',
}

export const ACTION_LABELS: Record<ActionType, string> = {
  change_status: 'Status ändern',
  add_label: 'Label hinzufügen',
  remove_label: 'Label entfernen',
  assign_user: 'Benutzer zuweisen',
  assign_role: 'Rolle zuweisen',
  send_discord_message: 'Discord-Nachricht senden',
  send_panel_notification: 'Panel-Benachrichtigung senden',
  create_reminder: 'Reminder erstellen',
  add_comment: 'Kommentar hinzufügen',
  change_priority: 'Priorität ändern',
}

/** `actor.*` ist bei allen nutzerausgelösten Triggern verfügbar (der Benutzer, der die
 * Änderung gemacht hat) — außer bei den zeitbasierten Triggern "Fälligkeitsdatum in < 24h"
 * und "Aufgabe überfällig", die vom Scheduler ohne Akteur ausgelöst werden. */
export const CONDITION_FIELD_SUGGESTIONS = [
  'task.priority',
  'task.progress',
  'task.title',
  'project.status',
  'actor.username',
]

export interface TemplateVariable {
  key: string
  description: string
}

/** Variablen, die in Templates (Discord-Nachricht, Panel-Benachrichtigung, Reminder,
 * Kommentar) per {{...}} genutzt werden können — siehe interpolateTemplate/getByPath in
 * automation.evaluator.ts, das jeden Punkt-Pfad im Auswertungskontext auflöst. Diese Liste
 * ist bewusst auf sinnvoll interpolierbare (skalare) Felder beschränkt — Arrays/Relationen
 * wie task.assignees oder task.labels ergeben als String keinen brauchbaren Text. Löst ein
 * Pfad nicht auf (z.B. label.* außerhalb eines Label-Triggers), bleibt der Platzhalter
 * unverändert im Text stehen statt zu verschwinden. */
export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  { key: 'task.title', description: 'Titel der Aufgabe' },
  { key: 'task.description', description: 'Beschreibung der Aufgabe' },
  { key: 'task.priority', description: 'Priorität: LOW, MEDIUM, HIGH oder CRITICAL' },
  { key: 'task.progress', description: 'Fortschritt in Prozent (Zahl)' },
  { key: 'task.dueDate', description: 'Fälligkeitsdatum' },
  { key: 'task.startDate', description: 'Startdatum' },
  { key: 'task.status.name', description: 'Name des aktuellen Status, z.B. "In Arbeit"' },
  { key: 'task.technicalScope', description: 'Technischer Scope der Aufgabe' },
  { key: 'task.definitionOfDone', description: 'Definition of Done der Aufgabe' },
  { key: 'project.name', description: 'Name des Projekts' },
  { key: 'project.status', description: 'Projektstatus: PLANNING, ACTIVE, PAUSED, COMPLETED oder ARCHIVED' },
  { key: 'label.name', description: 'Name des Labels — nur verfügbar beim Trigger "Label hinzugefügt"/"Label entfernt"' },
  {
    key: 'actor.displayName',
    description: 'Anzeigename des Benutzers, der die Änderung ausgelöst hat — nicht verfügbar bei zeitbasierten Triggern (fällig/überfällig)',
  },
  { key: 'actor.username', description: 'Benutzername des Benutzers, der die Änderung ausgelöst hat' },
]

export const OP_LABELS: Record<string, string> = {
  eq: 'ist gleich',
  neq: 'ist nicht gleich',
  in: 'ist einer von',
  not_in: 'ist keiner von',
  gt: 'ist größer als',
  lt: 'ist kleiner als',
  contains: 'enthält',
}
